'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/navigation'
import { toast } from '@warfighter/ui'
import { fetchHeatmapCities, fetchChildCities, searchCities } from '@/app/actions'
import { createBrowserSupabaseClient } from '@/lib/supabaseClient'
import MainNav from '@/app/components/MainNav'
import CityCard from '@/app/components/CityCard'
import CityDetailModalMarkets from '@/app/components/CityDetailModalMarkets'
import HeatmapMap from '@/app/components/HeatmapMap'
import { Search, X, ChevronDown, Map, LayoutGrid, RefreshCw } from 'lucide-react'

const CARDS_PER_PAGE = 24

type SortOption = 'default' | 'pop_desc' | 'pop_asc' | 'alpha_az' | 'alpha_za' | 'price_low' | 'price_high' | 'best_match' | 'affordability_low' | 'affordability_high'

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'best_match', label: 'Best Match' },
  { value: 'pop_desc', label: 'Population: High → Low' },
  { value: 'pop_asc', label: 'Population: Low → High' },
  { value: 'alpha_az', label: 'Alphabetical: A → Z' },
  { value: 'alpha_za', label: 'Alphabetical: Z → A' },
  { value: 'price_low', label: 'Home Price: Low → High' },
  { value: 'price_high', label: 'Home Price: High → Low' },
  { value: 'affordability_low', label: 'Affordability: Lowest → Highest' },
  { value: 'affordability_high', label: 'Affordability: Highest → Lowest' },
]

type FilterMode = 'off' | 'and' | 'or' | 'not'

type FilterFlags = {
  no_state_tax: FilterMode
  affordable_housing: FilterMode
  high_income: FilterMode
  big_city: FilterMode
  small_town: FilterMode
  pro_gun: FilterMode
  near_va_hospital: FilterMode
  veterans_top_50: FilterMode
  tech_hub: FilterMode
  military_hub: FilterMode
  lgbtq_friendly: FilterMode
  eco_friendly: FilterMode
  walkable: FilterMode
  low_crime: FilterMode
  good_schools: FilterMode
}

const FILTER_CYCLE: FilterMode[] = ['and', 'or', 'not', 'off']

const filterModeLabel: Record<FilterMode, string> = {
  off: '',
  and: 'AND',
  or: 'OR',
  not: 'NOT',
}

const filterModeColors: Record<FilterMode, string> = {
  off: 'border-[var(--chip-border)] bg-[var(--chip-bg)] text-[var(--chip-text)] hover:bg-[var(--surface-2)]',
  and: 'border-emerald-400 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  or: 'border-blue-400 bg-blue-500/15 text-blue-700 dark:text-blue-300',
  not: 'border-red-400 bg-red-500/15 text-red-700 dark:text-red-300',
}

export type CityDatum = {
  id: number
  slug: string | null
  rank_population: number | null
  primary_city: string
  primary_state: string
  hero_image_url: string | null
  lat: number | null
  lng: number | null
  median_home_price: number | null
  median_household_income: number | null
  no_state_income_tax: boolean
  cheap_housing: boolean
  high_salaries: boolean
  gun_friendly: boolean
  near_va_medical: boolean
  veterans_top_50_rank: number | null
  tech_hub: boolean
  military_hub: boolean
  primary_zip: string | null
  parent_city_id: number | null
  child_count: number
  description: string | null
  // Census-driven fields
  population: number | null
  affordability_ratio: number | null
  income_percentile: number | null
  lgbtq_score: number | null
  eco_score: number | null
  epa_aqi_median: number | null
  pct_public_transit: number | null
  pct_walk_bike: number | null
  // Crime data
  violent_crime_rate: number | null
  property_crime_rate: number | null
  crime_score: number | null
  // School data
  graduation_rate: number | null
  student_teacher_ratio: number | null
  per_pupil_spending: number | null
  school_score: number | null
  // VA facility
  nearest_va_distance_miles: number | null
  nearest_va_facility_name: string | null
  sports: {
    nfl: boolean
    nba: boolean
    mlb: boolean
    nhl: boolean
    mls: boolean
  }
}

const normalizeHeroImageUrl = (value: unknown) => {
  if (value == null) return null
  const trimmed = String(value).trim().replace(/^['"]|['"]$/g, '')
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') return null
  return trimmed
}

function normalizeRow(row: any): CityDatum {
  const economics = Array.isArray(row.city_economics)
    ? row.city_economics[0]
    : row.city_economics
  const features = Array.isArray(row.city_features)
    ? row.city_features[0]
    : row.city_features
  const sports = Array.isArray(row.city_sports) ? row.city_sports[0] : row.city_sports
  const descriptions = Array.isArray(row.city_descriptions)
    ? row.city_descriptions[0]
    : row.city_descriptions
  const census = Array.isArray(row.city_census)
    ? row.city_census[0]
    : row.city_census
  const childCount = typeof row.child_count === 'number' ? row.child_count : 0
  return {
    id: row.city_id,
    slug: row.slug ?? null,
    rank_population: row.rank_population ?? null,
    primary_city: row.primary_city,
    primary_state: row.primary_state ? row.primary_state.trim().toUpperCase() : '',
    hero_image_url: normalizeHeroImageUrl(row.hero_image_url),
    lat: row.latitude != null ? Number(row.latitude) : null,
    lng: row.longitude != null ? Number(row.longitude) : null,
    median_home_price: economics?.median_home_price ?? (census?.median_home_value != null ? Number(census.median_home_value) : null),
    median_household_income: economics?.median_hhi ?? (census?.median_household_income != null ? Number(census.median_household_income) : null),
    no_state_income_tax: Boolean(economics?.no_state_income_tax),
    cheap_housing: Boolean(economics?.cheap_housing),
    high_salaries: Boolean(economics?.high_salaries),
    gun_friendly: Boolean(features?.gun_friendly),
    near_va_medical: Boolean(features?.near_va_medical),
    veterans_top_50_rank: features?.veterans_top_50_rank ?? null,
    tech_hub: Boolean(features?.tech_hub),
    military_hub: Boolean(features?.military_hub),
    primary_zip: row.primary_zip ?? null,
    parent_city_id: row.parent_city_id ?? null,
    child_count: childCount,
    description: descriptions?.description ?? null,
    population: census?.population ?? null,
    affordability_ratio: census?.affordability_ratio != null ? Number(census.affordability_ratio) : null,
    income_percentile: census?.income_percentile != null ? Number(census.income_percentile) : null,
    lgbtq_score: census?.lgbtq_score != null ? Number(census.lgbtq_score) : null,
    eco_score: census?.eco_score != null ? Number(census.eco_score) : null,
    epa_aqi_median: census?.epa_aqi_median != null ? Number(census.epa_aqi_median) : null,
    pct_public_transit: census?.pct_public_transit != null ? Number(census.pct_public_transit) : null,
    pct_walk_bike: census?.pct_walk_bike != null ? Number(census.pct_walk_bike) : null,
    violent_crime_rate: census?.violent_crime_rate != null ? Number(census.violent_crime_rate) : null,
    property_crime_rate: census?.property_crime_rate != null ? Number(census.property_crime_rate) : null,
    crime_score: census?.crime_score != null ? Number(census.crime_score) : null,
    graduation_rate: census?.graduation_rate != null ? Number(census.graduation_rate) : null,
    student_teacher_ratio: census?.student_teacher_ratio != null ? Number(census.student_teacher_ratio) : null,
    per_pupil_spending: census?.per_pupil_spending != null ? Number(census.per_pupil_spending) : null,
    school_score: census?.school_score != null ? Number(census.school_score) : null,
    nearest_va_distance_miles: features?.nearest_va_distance_miles != null ? Number(features.nearest_va_distance_miles) : null,
    nearest_va_facility_name: features?.nearest_va_facility_name ?? null,
    sports: {
      nfl: Boolean(sports?.nfl),
      nba: Boolean(sports?.nba),
      mlb: Boolean(sports?.mlb),
      nhl: Boolean(sports?.nhl),
      mls: Boolean(sports?.mls)
    }
  }
}

const filterOptions = [
  { key: 'veterans_top_50', label: 'Veterans Top 50', icon: '⭐' },
  { key: 'no_state_tax', label: 'No State Tax', icon: '💸' },
  { key: 'affordable_housing', label: 'Affordable', icon: '🏠' },
  { key: 'high_income', label: 'High Income', icon: '💰' },
  { key: 'big_city', label: 'Big City', icon: '🏙️' },
  { key: 'small_town', label: 'Small Town', icon: '🏡' },
  { key: 'tech_hub', label: 'Tech Hub', icon: '🧠' },
  { key: 'military_hub', label: 'Military Hub', icon: '🪖' },
  { key: 'pro_gun', label: 'Pro-Gun', icon: '🔫' },
  { key: 'near_va_hospital', label: 'Near VA Hospital', icon: '🩺' },
  { key: 'lgbtq_friendly', label: 'LGBTQ Friendly', icon: '🏳️‍🌈' },
  { key: 'eco_friendly', label: 'Eco-Friendly', icon: '🌿' },
  { key: 'walkable', label: 'Walkable', icon: '🚶' },
  { key: 'low_crime', label: 'Low Crime', icon: '🛡️' },
  { key: 'good_schools', label: 'Good Schools', icon: '🎓' },
] as const

export default function MarketsPage() {
  const router = useRouter()
  const supabase = useMemo(() => createBrowserSupabaseClient(), [])
  const [cities, setCities] = useState<CityDatum[]>([])
  const [shuffledCities, setShuffledCities] = useState<CityDatum[]>([])
  const [citiesLoading, setCitiesLoading] = useState(false)
  const [citiesError, setCitiesError] = useState('')
  const [stateFilter, setStateFilter] = useState<string | null>(null)
  const [mobileView, setMobileView] = useState<'list' | 'map'>('list')
  const [showMap, setShowMap] = useState(false)
  const [sortBy, setSortBy] = useState<SortOption>('default')
  const [filters, setFilters] = useState<FilterFlags>({
    no_state_tax: 'off',
    affordable_housing: 'off',
    high_income: 'off',
    big_city: 'off',
    small_town: 'off',
    pro_gun: 'off',
    near_va_hospital: 'off',
    veterans_top_50: 'off',
    tech_hub: 'off',
    military_hub: 'off',
    lgbtq_friendly: 'off',
    eco_friendly: 'off',
    walkable: 'off',
    low_crime: 'off',
    good_schools: 'off',
  })
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({})

  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<CityDatum[] | null>(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Child cities cache
  const [childrenMap, setChildrenMap] = useState<Record<number, CityDatum[]>>({})
  const [loadingChildren, setLoadingChildren] = useState<number | null>(null)

  // Pagination state – only render a slice of visible cards
  const [visibleCount, setVisibleCount] = useState(CARDS_PER_PAGE)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  // Detail modal state
  const [selectedCityForDetail, setSelectedCityForDetail] = useState<CityDatum | null>(null)
  const [detailLoadingChildren, setDetailLoadingChildren] = useState<number | null>(null)

  const scrollToCityCard = (cityId: number) => {
    const node = cardRefs.current[cityId]
    if (!node) return
    node.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const hasActiveFilters = Object.values(filters).some((v) => v !== 'off')

  const getFilterMatchCount = (city: CityDatum) => {
    const entries = Object.entries(filters) as [keyof FilterFlags, FilterMode][]
    return entries.filter(([key, mode]) => mode !== 'off' && mode !== 'not' && getCityFilterValue(city, key)).length
  }

  const applySorting = (list: CityDatum[], sort: SortOption): CityDatum[] => {
    if (sort === 'default') return list
    return [...list].sort((a, b) => {
      switch (sort) {
        case 'pop_desc':
          return (b.rank_population ?? 0) - (a.rank_population ?? 0)
        case 'pop_asc':
          return (a.rank_population ?? 0) - (b.rank_population ?? 0)
        case 'alpha_az':
          return a.primary_city.localeCompare(b.primary_city)
        case 'alpha_za':
          return b.primary_city.localeCompare(a.primary_city)
        case 'price_low':
          return (a.median_home_price ?? Infinity) - (b.median_home_price ?? Infinity)
        case 'price_high':
          return (b.median_home_price ?? 0) - (a.median_home_price ?? 0)
        case 'affordability_low':
          return (a.affordability_ratio ?? -Infinity) - (b.affordability_ratio ?? -Infinity)
        case 'affordability_high':
          return (b.affordability_ratio ?? 0) - (a.affordability_ratio ?? 0)
        case 'best_match':
          return getFilterMatchCount(b) - getFilterMatchCount(a)
        default:
          return 0
      }
    })
  }

  const getCityFilterValue = (city: CityDatum, key: string): boolean => {
    switch (key) {
      case 'no_state_tax': return city.no_state_income_tax
      case 'affordable_housing': return city.affordability_ratio != null && city.affordability_ratio >= 2.75 && city.affordability_ratio <= 4.5
      case 'high_income': return city.income_percentile != null && city.income_percentile >= 85
      case 'big_city': {
        const pop = city.population ?? city.rank_population
        return pop != null && pop >= 500_000
      }
      case 'small_town': {
        const pop = city.population ?? city.rank_population
        return pop != null && pop < 50_000
      }
      case 'pro_gun': return city.gun_friendly
      case 'near_va_hospital': return city.near_va_medical
      case 'veterans_top_50': return city.veterans_top_50_rank != null
      case 'tech_hub': return city.tech_hub
      case 'military_hub': return city.military_hub
      case 'lgbtq_friendly': return city.lgbtq_score != null && city.lgbtq_score >= 60
      case 'eco_friendly': return city.eco_score != null && city.eco_score >= 60
      case 'walkable': {
        const pop = city.population ?? city.rank_population ?? 0
        if (pop < 10_000) return false // exclude tiny towns with statistically unreliable percentages
        const combined = (city.pct_public_transit ?? 0) + (city.pct_walk_bike ?? 0)
        return combined >= 10
      }
      case 'low_crime': return city.crime_score != null && city.crime_score >= 70
      case 'good_schools': return city.school_score != null && city.school_score >= 70
      default: return false
    }
  }

  const cityMatchesFilters = (city: CityDatum) => {
    if (!hasActiveFilters) return true

    const entries = Object.entries(filters) as [keyof FilterFlags, FilterMode][]
    const andFilters = entries.filter(([, mode]) => mode === 'and')
    const orFilters = entries.filter(([, mode]) => mode === 'or')
    const notFilters = entries.filter(([, mode]) => mode === 'not')

    // AND: city must match ALL
    for (const [key] of andFilters) {
      if (!getCityFilterValue(city, key)) return false
    }

    // NOT: city must NOT match ANY
    for (const [key] of notFilters) {
      if (getCityFilterValue(city, key)) return false
    }

    // OR: city must match AT LEAST ONE (only if there are OR filters)
    if (orFilters.length > 0) {
      const matchesAnyOr = orFilters.some(([key]) => getCityFilterValue(city, key))
      if (!matchesAnyOr) return false
    }

    return true
  }

  const visibleCities = useMemo(() => {
    // If searching, show search results
    if (searchResults !== null) {
      const filtered = searchResults.filter((city) => cityMatchesFilters(city))
      const stateFiltered = stateFilter
        ? filtered.filter((city) => city.primary_state === stateFilter)
        : filtered
      return applySorting(stateFiltered, sortBy)
    }
    const useShuffled = sortBy === 'default' && !hasActiveFilters && !stateFilter
    const baseCities = useShuffled && shuffledCities.length ? shuffledCities : cities
    const filtered = baseCities.filter((city) => cityMatchesFilters(city))
    const stateFiltered = stateFilter
      ? filtered.filter((city) => city.primary_state === stateFilter)
      : filtered
    return applySorting(stateFiltered, sortBy)
  }, [cities, hasActiveFilters, filters, shuffledCities, stateFilter, searchResults, sortBy])

  const paginatedCities = useMemo(
    () => visibleCities.slice(0, visibleCount),
    [visibleCities, visibleCount]
  )
  const hasMore = visibleCount < visibleCities.length

  const handleCitySelect = (city: CityDatum) => {
    if (city.slug) {
      router.push(`/market/${city.slug}`)
    } else {
      scrollToCityCard(city.id)
    }
  }

  const handleStartScenario = (city: CityDatum) => {
    if (city.primary_zip) {
      router.push(`/calculator/transition?targetZip=${encodeURIComponent(city.primary_zip)}`)
    } else {
      toast.info('No ZIP found for this city.')
    }
  }

  const handleExpandChildren = useCallback(async (parentId: number) => {
    if (childrenMap[parentId]) return // Already loaded
    setLoadingChildren(parentId)
    try {
      const rows = await fetchChildCities(parentId)
      const normalized = rows.map(normalizeRow)
      setChildrenMap((prev) => ({ ...prev, [parentId]: normalized }))
    } catch {
      // Silently fail — user can retry
    } finally {
      setLoadingChildren(null)
    }
  }, [childrenMap])

  const handleSelectCityForDetail = useCallback(async (city: CityDatum) => {
    setSelectedCityForDetail(city)
  }, [])

  const handleExpandChildrenForDetail = useCallback(async (parentId: number) => {
    if (childrenMap[parentId]) return // Already loaded
    setDetailLoadingChildren(parentId)
    try {
      const rows = await fetchChildCities(parentId)
      const normalized = rows.map(normalizeRow)
      setChildrenMap((prev) => ({ ...prev, [parentId]: normalized }))
    } catch {
      // Silently fail — user can retry
    } finally {
      setDetailLoadingChildren(null)
    }
  }, [childrenMap])

  // Debounced search
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)

    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults(null)
      setSearchLoading(false)
      return
    }

    setSearchLoading(true)
    searchTimerRef.current = setTimeout(async () => {
      try {
        const rows = await searchCities(searchQuery.trim())
        const normalized = rows.map(normalizeRow)
        setSearchResults(normalized)
      } catch {
        setSearchResults([])
      } finally {
        setSearchLoading(false)
      }
    }, 300)

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    }
  }, [searchQuery])

  // Default order: sort by population descending for consistent loading by market size
  useEffect(() => {
    if (cities.length === 0) {
      setShuffledCities([])
      return
    }
    const sorted = [...cities].sort((a, b) => {
      const popA = a.population ?? a.rank_population ?? 0
      const popB = b.population ?? b.rank_population ?? 0
      return popB - popA
    })
    setShuffledCities(sorted)
  }, [cities])

  // Reset visible count when filters, sort, search, or state filter changes
  useEffect(() => {
    setVisibleCount(CARDS_PER_PAGE)
  }, [filters, sortBy, searchResults, stateFilter])

  // Infinite scroll: observe the sentinel element
  useEffect(() => {
    const node = loadMoreRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((prev) => prev + CARDS_PER_PAGE)
        }
      },
      { rootMargin: '400px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [visibleCities.length]) // re-attach when the filtered list changes

  useEffect(() => {
    const loadCities = async () => {
      setCitiesLoading(true)
      setCitiesError('')
      try {
        const { data } = await supabase.auth.getSession()
        const accessToken = data.session?.access_token
        const rows = await fetchHeatmapCities({ accessToken })
        const normalized = rows.map(normalizeRow)
        setCities(normalized)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error'
        setCitiesError(message)
      }
      setCitiesLoading(false)
    }

    loadCities()
  }, [supabase])

  return (
    <div className="space-y-6">
      <MainNav />

      <div className="mx-auto max-w-6xl space-y-6">
        {/* Header — scrolls away */}
        <section className="space-y-1">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-extrabold text-[var(--text-1)]">Find your Destination</h1>
              <p className="text-sm leading-relaxed text-[var(--text-2)]">
                Browse cities, filter by your priorities, and start a scenario.
              </p>
            </div>
            {stateFilter && (
              <button
                type="button"
                onClick={() => setStateFilter(null)}
                className="rounded-full border border-[var(--border)] px-3 py-1 text-xs font-semibold text-[var(--text-2)] transition hover:bg-[var(--surface-2)] hover:text-[var(--text-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
              >
                Clear {stateFilter}
              </button>
            )}
          </div>
        </section>

        {/* Search + Filters — sticks to top on scroll */}
        <section className="sticky top-0 z-30 space-y-4 bg-[var(--bg)] pb-3 pt-3 -mt-3 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)]">
          {/* Search + Sort row */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-3)]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search cities by name..."
                className="w-full rounded-xl border border-[var(--border)] bg-[var(--surface-1)] py-2.5 pl-10 pr-10 text-sm text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-3)] hover:text-[var(--text-1)]"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="relative shrink-0">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="appearance-none rounded-xl border border-[var(--border)] bg-[var(--surface-1)] py-2.5 pl-3 pr-8 text-sm font-medium text-[var(--text-2)] transition hover:border-[var(--accent)] focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20"
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-3)]" />
            </div>
            <button
              type="button"
              onClick={() => setShowMap((prev) => !prev)}
              className={`hidden md:flex items-center gap-1.5 shrink-0 rounded-xl border px-3 py-2.5 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]/20 ${
                showMap
                  ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)]'
                  : 'border-[var(--border)] bg-[var(--surface-1)] text-[var(--text-2)] hover:border-[var(--accent)]'
              }`}
              title={showMap ? 'Hide map' : 'Show map'}
            >
              {showMap ? <LayoutGrid className="h-4 w-4" /> : <Map className="h-4 w-4" />}
              <span>{showMap ? 'Grid' : 'Map'}</span>
            </button>
          </div>

          <div className="relative">
            <div className="flex gap-3 overflow-x-auto pb-2 pr-16 scroll-px-6 hide-scrollbar">
              {filterOptions.map((option) => {
                const mode = filters[option.key]
                const active = mode !== 'off'
                return (
                  <div key={option.key} className="relative flex items-center">
                    <button
                      type="button"
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          [option.key]: prev[option.key] === 'off' ? 'and' : 'off'
                        }))
                      }
                      className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 pr-16 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] ${
                        filterModeColors[mode]
                      }`}
                    >
                      <span
                        className={`transition ${
                          active ? 'opacity-100' : 'opacity-60 grayscale'
                        }`}
                      >
                        {option.icon}
                      </span>
                      <span>{option.label}</span>
                    </button>
                    {active && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setFilters((prev) => {
                            const currentIdx = FILTER_CYCLE.indexOf(prev[option.key])
                            const nextMode = FILTER_CYCLE[(currentIdx + 1) % FILTER_CYCLE.length]
                            return { ...prev, [option.key]: nextMode }
                          })
                        }}
                        className={`absolute right-1.5 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide transition hover:opacity-80 ${
                          mode === 'and' ? 'bg-emerald-500/25 text-emerald-700 dark:text-emerald-300' :
                          mode === 'or' ? 'bg-blue-500/25 text-blue-700 dark:text-blue-300' :
                          'bg-red-500/25 text-red-700 dark:text-red-300'
                        }`}
                        title="Click to cycle: AND → OR → NOT → off"
                      >
                        {filterModeLabel[mode]}
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[var(--bg)] to-transparent" />
          </div>

          {/* Search feedback (only when actively searching) */}
          {searchLoading ? (
            <div className="text-xs text-[var(--text-3)]">Searching...</div>
          ) : searchResults !== null ? (
            <div className="text-xs text-[var(--text-3)]">Found {visibleCities.length} cities matching &quot;{searchQuery}&quot;</div>
          ) : null}
        </section>

        <section className={`grid gap-6 md:h-[calc(100vh-200px)] md:overflow-hidden ${showMap ? 'md:grid-cols-[3fr_2fr]' : 'md:grid-cols-1'}`}>
          <div
            className={
              mobileView === 'map'
                ? 'hidden md:block space-y-4 md:h-full md:overflow-y-auto md:pr-2 hide-scrollbar'
                : 'space-y-4 md:h-full md:overflow-y-auto md:pr-2 hide-scrollbar'
            }
          >
          {/* Skeleton loader while cities are loading */}
          {citiesLoading && (
            <div className={`grid gap-4 ${showMap ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={`skeleton-${i}`}
                  className="overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] shadow-sm"
                >
                  <div className="h-48 animate-pulse bg-gradient-to-br from-[var(--surface-2)] via-[var(--surface-3)] to-[var(--surface-2)]" />
                  <div className="space-y-3 p-4">
                    <div className="h-5 w-3/4 animate-pulse rounded bg-[var(--surface-2)]" />
                    <div className="h-4 w-1/2 animate-pulse rounded bg-[var(--surface-2)]" />
                    <div className="flex gap-2">
                      <div className="h-5 w-16 animate-pulse rounded-full bg-[var(--surface-2)]" />
                      <div className="h-5 w-20 animate-pulse rounded-full bg-[var(--surface-2)]" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Error state with retry */}
          {citiesError && (
            <div className="rounded-xl border border-red-400/30 bg-red-500/10 p-6 text-center">
              <p className="text-sm font-medium text-red-400">
                {citiesError.includes('503') || citiesError.includes('Service Unavailable')
                  ? 'The server is temporarily busy. This usually resolves in a few seconds.'
                  : citiesError}
              </p>
              <button
                type="button"
                onClick={() => {
                  setCitiesError('')
                  setCitiesLoading(true)
                  ;(async () => {
                    try {
                      const { data } = await supabase.auth.getSession()
                      const accessToken = data.session?.access_token
                      const rows = await fetchHeatmapCities({ accessToken })
                      const normalized = rows.map(normalizeRow)
                      setCities(normalized)
                    } catch (err) {
                      const message = err instanceof Error ? err.message : 'Unknown error'
                      setCitiesError(message)
                    }
                    setCitiesLoading(false)
                  })()
                }}
                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-red-400/30 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-400 transition hover:bg-red-500/20"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </button>
            </div>
          )}
          {!citiesLoading && !citiesError && visibleCities.length === 0 && (
            <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] p-4 text-sm text-[var(--text-2)]">
              No cities match the current filters.
            </div>
          )}
          <div className={`grid gap-4 ${showMap ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
            {paginatedCities.map((city) => (
              <div
                key={city.id}
                ref={(node) => {
                  cardRefs.current[city.id] = node
                }}
              >
                <CityCard
                  city={city}
                  href={city.slug ? `/market/${city.slug}` : undefined}
                />
              </div>
            ))}
          </div>
          {/* Infinite scroll sentinel + show more button */}
          {hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-6">
              <button
                type="button"
                onClick={() => setVisibleCount((prev) => prev + CARDS_PER_PAGE)}
                className="rounded-xl border border-[var(--border)] bg-[var(--surface-1)] px-6 py-2.5 text-sm font-medium text-[var(--text-2)] transition hover:bg-[var(--surface-2)] hover:text-[var(--text-1)]"
              >
                Show more ({visibleCities.length - visibleCount} remaining)
              </button>
            </div>
          )}
          </div>

          {showMap && (
            <div className={mobileView === 'list' ? 'hidden md:block md:h-full' : 'block'}>
              <div className="h-full rounded-2xl border border-[var(--border)] bg-[var(--surface-1)] shadow-lg dark:shadow-none">
                <HeatmapMap
                  cities={visibleCities}
                  selectedState={stateFilter}
                  onStateSelect={setStateFilter}
                  onCitySelect={handleCitySelect}
                />
              </div>
            </div>
          )}
        </section>
      </div>

      {/* City Detail Modal */}
      {selectedCityForDetail && (
        <CityDetailModalMarkets
          city={selectedCityForDetail}
          description={selectedCityForDetail.description}
          childCities={childrenMap[selectedCityForDetail.id]}
          loadingChildren={detailLoadingChildren === selectedCityForDetail.id}
          onExpandChildren={() => handleExpandChildrenForDetail(selectedCityForDetail.id)}
          onClose={() => setSelectedCityForDetail(null)}
          onStartScenario={() => handleStartScenario(selectedCityForDetail)}
        />
      )}

      <button
        type="button"
        onClick={() => setMobileView((prev) => (prev === 'list' ? 'map' : 'list'))}
        className="fixed bottom-6 left-1/2 z-20 -translate-x-1/2 rounded-full bg-[var(--accent)] px-6 py-3 text-sm font-semibold text-[var(--accent-contrast)] shadow-lg transition hover:bg-[var(--accent-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] md:hidden"
      >
        {mobileView === 'list' ? 'Show Map' : 'Show List'}
      </button>
    </div>
  )
}
